import { industries } from "@/src/features/account/industryDetails";
import { v4 as uuidv4 } from "uuid";
import { resolveCalendarTimezone } from "@/src/features/calendar/core/userClock";

export const genUniqueId = () => {
  const timestamp = Date.now();
  const random = uuidv4();
  return `${timestamp}-${random}`;
};

export const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) {
    return text;
  }

  const truncatedText = text.substring(0, maxLength) + "...";

  return truncatedText;
};

export const getIndustryDetails = (industry: string) => {
  return industries[industry];
};

export function flatObject(obj: any, prefix = "") {
  let flattened: any = {};

  for (let key in obj) {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
      Object.assign(flattened, flatObject(obj[key], newKey));
    } else {
      flattened[newKey] = obj[key];
    }
  }

  return flattened;
}

export function calculateAge(dateOfBirth: string) {
  const dateOfBirthArray = dateOfBirth.split(" ");
  const birthdate = new Date(dateOfBirthArray.at(-1) ?? "");
  const currentDate = new Date();

  const timeDiff = currentDate.getTime() - birthdate.getTime();
  const age = Math.floor(timeDiff / (365 * 24 * 60 * 60 * 1000));

  return age;
}

function isMergeableObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * `obj2` laid over `obj1`, recursing where both sides hold an object. A `null`
 * in `obj2` never erases an object in `obj1`; anywhere else `obj2` wins as is,
 * so `null` over `null` stays `null` (it once became `{}`, which made an unset
 * session location look like a position).
 */
export function deepMerge(obj1: any, obj2: any) {
  const output = { ...obj1 };
  if (!isMergeableObject(obj2)) return output;
  for (const key of Object.keys(obj2)) {
    const base = isMergeableObject(obj1) ? obj1[key] : undefined;
    const value = obj2[key];
    if (isMergeableObject(base) && isMergeableObject(value)) {
      output[key] = deepMerge(base, value);
    } else if (isMergeableObject(base) && value === null) {
      output[key] = { ...base };
    } else {
      output[key] = value;
    }
  }
  return output;
}

export function isValidLuhn(input: number) {
  // Convert the input into an array of numbers
  const digits = input
    .toString()
    .split("")
    .map((num) => Number.parseInt(num, 10));

  let sum = 0;
  let alternate = false;

  // Start from the rightmost digit
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = digits[i];
    if (alternate) {
      n *= 2;
      if (n > 9) {
        n -= 9;
      }
    }
    sum += n;
    alternate = !alternate;
  }

  // The number is valid if the sum is divisible by 10
  return sum % 10 === 0;
}

export function getRandomHexColor() {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

/**
 * One stored conversation turn, as the typed silo read returns it. Every field
 * is optional because the silo declares them so: `thresholdTokens` is not even
 * a column and arrives through the row's extra fields.
 */
interface ChatHistoryItem {
  question?: string;
  textAnswer?: string;
  audioAnswer?: string;
  answer?: unknown;
  thresholdTokens?: number;
  [field: string]: unknown;
}
export function getPreviousChat(history: ChatHistoryItem[]) {
  const augmentedContexts = [];

  // Start from the beginning of the history array
  for (let i = 0; i <= history.length - 1; i++) {
    const item = history[i];

    if (item.thresholdTokens === 0 || i === history.length - 1) {
      augmentedContexts.push(`question: ${item.question}\n\n answer: ${item.textAnswer}`);
      break;
    }

    augmentedContexts.push(`question: ${item.question}\n\n answer: ${item.textAnswer}`);
  }

  return augmentedContexts.join("\n\n---\n\n") + "\n\n-----\n\n";
}

export const orderKeysByProxy = (obj: any, order: any) => {
  return new Proxy(obj, {
    ownKeys: function (target) {
      return order.concat(Reflect.ownKeys(target).filter((key) => !order.includes(key)));
    },
  });
};

export const checkUserAgent = (userAgent: string) => {
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());

  if (isMobile) {
    return "mobile";
  }
  return "browser";
};

/**
 * User timezone utilities for calculating local time based on user preferences.
 * Uses IANA timezone format (e.g., 'America/New_York', 'Europe/London').
 */

export interface UserCurrentTime {
  iso: string;           // ISO 8601 format in user's timezone
  timestamp: number;     // Unix timestamp (always UTC)
  date: string;          // YYYY-MM-DD in user's timezone
  time: string;          // HH:MM:SS in user's timezone
  dayOfWeek: string;     // Full day name (e.g., 'Monday')
  formatted: string;     // Human-readable format
  timezone: string;      // The timezone used
  utcOffset: string;     // UTC offset (e.g., '-05:00')
}

/**
 * Calculate the current time in the user's timezone.
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns UserCurrentTime object with various time representations
 */
export function getUserCurrentTime(timezone?: string): UserCurrentTime {
  const now = new Date();

  // An invalid or missing zone reads as UTC, never a guessed city.
  const validTimezone = resolveCalendarTimezone({ explicit: timezone }).timezone;
  
  // Get components in user's timezone
  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: validTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  
  const timeFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: validTimezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  
  const dayFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: validTimezone,
    weekday: 'long',
  });
  
  const fullFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: validTimezone,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  
  // Calculate UTC offset for the timezone
  const utcOffset = getUTCOffset(now, validTimezone);
  
  // Create ISO string in user's timezone
  const date = dateFormatter.format(now); // YYYY-MM-DD
  const time = timeFormatter.format(now); // HH:MM:SS
  const iso = `${date}T${time}${utcOffset}`;
  
  return {
    iso,
    timestamp: now.getTime(),
    date,
    time,
    dayOfWeek: dayFormatter.format(now),
    formatted: fullFormatter.format(now),
    timezone: validTimezone,
    utcOffset,
  };
}

/**
 * Get the UTC offset string for a specific timezone at a given time.
 * @param date - The date to check
 * @param timezone - IANA timezone string
 * @returns UTC offset string (e.g., '-05:00', '+02:00')
 */
function getUTCOffset(date: Date, timezone: string): string {
  // Get the time in both UTC and the target timezone
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
  
  // Calculate offset in minutes
  const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / 60000;
  
  // Format as ±HH:MM
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absMinutes / 60).toString().padStart(2, '0');
  const minutes = (absMinutes % 60).toString().padStart(2, '0');
  
  return `${sign}${hours}:${minutes}`;
}

/**
 * Convert a Date object to a specific timezone.
 * @param date - The date to convert
 * @param timezone - IANA timezone string
 * @returns New Date object representing the same instant in the target timezone
 */
export function convertToTimezone(date: Date, timezone: string): Date {
  return new Date(date.toLocaleString('en-US', { timeZone: timezone }));
}

/**
 * Check if a given timezone string is valid.
 * @param timezone - IANA timezone string to validate
 * @returns boolean indicating if the timezone is valid
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}
